import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Snackbar,
  Typography,
} from '@mui/material';

import {
  LOUNGE_SURVEY_QUESTIONS,
  QuestionPurpose,
} from '../constants/loungeQuestions';
import {
  SurveyQuestionCard,
  SurveyOptionalQuestions,
} from '../components/lounge-survey';
import { useLoungeTokenExpiration } from '../hooks/useLoungeTokenExpiration';
import { useLoungeSurveySubmit } from '../hooks/useLoungeSurveySubmit';
import { getLoungeTokenInfo } from '../utils/loungeToken';

const LoungeSurveyScreen = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Decode URL params
  const encodedData = searchParams.get('data');
  let masseuseName = 'Your Masseuse';
  let customerName = 'Guest';

  try {
    if (encodedData) {
      const decodedData = JSON.parse(atob(encodedData));
      masseuseName = decodedData.masseuseName || 'Your Masseuse';
      customerName = decodedData.customerName || 'Guest';
    }
  } catch (error) {
    console.error('Failed to decode URL params:', error);
    // Redirect back to lounge access if params are invalid
    navigate('/lounge-access');
  }

  // Token expiration monitoring - redirects to /lounge-access when token expires
  useLoungeTokenExpiration({
    redirectPath: '/lounge-access',
    onExpire: () => {
      console.log('Lounge token expired, redirecting to login');
    },
  });

  // Track remaining time for display
  const [remainingTime, setRemainingTime] = useState<number>(0);

  // Update remaining time every second
  useEffect(() => {
    const updateTime = () => {
      const info = getLoungeTokenInfo();
      setRemainingTime(info?.remainingMs ?? 0);
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);

    return () => clearInterval(interval);
  }, []);

  // Format remaining time as MM:SS
  const formattedTime = useMemo(() => {
    const minutes = Math.floor(remainingTime / 60000);
    const seconds = Math.floor((remainingTime % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, [remainingTime]);

  // Answers state - using Map for O(1) lookups
  const [answers, setAnswers] = useState<Map<number, number | null>>(new Map());

  // Error/success states
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showError, setShowError] = useState(false);

  // Survey submission hook
  const { submit, isPending } = useLoungeSurveySubmit({
    onError: (error) => {
      setErrorMessage(
        error.message || 'Failed to submit survey. Please try again.',
      );
      setShowError(true);
    },
  });

  // Get the masseuse rating question (purpose === 1)
  const masseuseQuestion = useMemo(
    () =>
      LOUNGE_SURVEY_QUESTIONS.find(
        (q) => q.purpose === QuestionPurpose.MASSEUSE_RATING,
      ),
    [],
  );

  // Handle answer change
  const handleAnswerChange = useCallback(
    (questionId: number, value: number | null) => {
      setAnswers((prev) => {
        const newAnswers = new Map(prev);
        if (value === null) {
          newAnswers.delete(questionId);
        } else {
          newAnswers.set(questionId, value);
        }
        return newAnswers;
      });
    },
    [],
  );

  // Handle form submission
  const handleSubmit = useCallback(() => {
    // Validate that at least the masseuse rating is provided
    if (masseuseQuestion && !answers.has(masseuseQuestion.id)) {
      setErrorMessage(
        'Please rate your experience with the masseuse before submitting.',
      );
      setShowError(true);
      return;
    }

    // Validate masseuse rating value
    const masseuseRating = masseuseQuestion
      ? answers.get(masseuseQuestion.id)
      : null;
    if (masseuseQuestion && (masseuseRating === null || masseuseRating === 0)) {
      setErrorMessage('Please provide a rating for the masseuse.');
      setShowError(true);
      return;
    }

    submit(answers);
  }, [answers, masseuseQuestion, submit]);

  // Handle error snackbar close
  const handleCloseError = useCallback(() => {
    setShowError(false);
  }, []);

  // Array of background images to stack vertically
  const backgroundImages = [
    'https://clubdynasty.ca/wp-content/uploads/2023/05/Spa-1.jpg',
    'https://clubdynasty.ca/wp-content/uploads/2021/06/reception-18.jpg',
    'https://clubdynasty.ca/wp-content/uploads/2023/05/Lounge-2.jpg',
    'https://clubdynasty.ca/wp-content/uploads/2023/05/Lounge-5.jpg',
    'https://clubdynasty.ca/wp-content/uploads/2023/05/Spa-3.jpg',
    'https://clubdynasty.ca/wp-content/uploads/2021/06/reception-17.jpg',
    'https://clubdynasty.ca/wp-content/uploads/2023/05/Lounge-1.jpg',
    'https://clubdynasty.ca/wp-content/uploads/2023/05/Spa-2.jpg',
    'https://clubdynasty.ca/wp-content/uploads/2023/05/Steam.jpg',
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        backgroundColor: '#1a1a1a',
        py: { xs: 2, sm: 4, md: 5, lg: 6 },
        overflow: 'hidden',
      }}
    >
      {/* Stacked background images */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {backgroundImages.map((img, index) => (
          <Box
            key={index}
            sx={{
              flex: '0 0 auto',
              minHeight: { xs: '50vh', sm: '60vh', md: '70vh' },
              backgroundImage: `url(${img})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.55)',
              },
            }}
          />
        ))}
      </Box>
      <Container
        maxWidth="md"
        sx={{
          position: 'relative',
          zIndex: 1,
          px: { xs: 2, sm: 4, md: 5, lg: 6 },
        }}
      >
        {/* Welcome Header Card */}
        <Box
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            borderRadius: { xs: 2, sm: 3 },
            overflow: 'hidden',
            mb: { xs: 3, sm: 5, md: 6 },
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          }}
        >
          {/* Header - Gold Theme with Timer */}
          <Box
            sx={{
              background:
                'linear-gradient(135deg, #c9a227 0%, #d4af37 50%, #b8960f 100%)',
              p: { xs: 2, sm: 3, md: 3.5 },
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography
                variant="h5"
                sx={{
                  color: 'white',
                  fontSize: { xs: '1.1rem', sm: '1.5rem', md: '1.6rem' },
                  fontWeight: 700,
                }}
              >
                Welcome, {customerName}!
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: { xs: '0.8rem', sm: '0.95rem', md: '1rem' },
                  fontWeight: 500,
                  mt: 0.25,
                }}
              >
                Session Survey • Club Dynasty
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: { xs: 1.5, sm: 2 },
                py: { xs: 0.75, sm: 1 },
                borderRadius: 1.5,
                backgroundColor:
                  remainingTime < 60000
                    ? 'rgba(211, 47, 47, 0.98)'
                    : 'rgba(255, 255, 255, 0.25)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.9rem' },
                  fontWeight: 600,
                  color: 'white',
                }}
              >
                ⏱ {formattedTime}
              </Typography>
            </Box>
          </Box>

          {/* Simple Intro Text */}
          <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Typography
              variant="body1"
              sx={{
                fontSize: {
                  xs: '0.95rem',
                  sm: '1.1rem',
                  md: '1.15rem',
                },
                color: 'text.primary',
                lineHeight: 1.7,
                fontWeight: 500,
                textAlign: 'center',
              }}
            >
              Please take a moment to rate your experience with {masseuseName}.
              Your feedback helps us maintain excellence.
            </Typography>
          </Box>
        </Box>

        {/* Primary masseuse rating question */}
        {masseuseQuestion && (
          <Box sx={{ mb: { xs: 2, sm: 4, md: 5 } }}>
            <SurveyQuestionCard
              question={masseuseQuestion}
              value={answers.get(masseuseQuestion.id) ?? null}
              onChange={handleAnswerChange}
              masseuseName={masseuseName}
              disabled={isPending}
            />
          </Box>
        )}

        {/* Optional questions in accordion */}
        <SurveyOptionalQuestions
          answers={answers}
          onAnswerChange={handleAnswerChange}
          disabled={isPending}
        />

        {/* Action buttons */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: { xs: 2, sm: 4, md: 4 },
            mt: { xs: 3, sm: 6, md: 7 },
            mb: { xs: 4, sm: 6, md: 7 },
          }}
        >
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/lounge-access')}
            disabled={isPending}
            sx={{
              minWidth: { xs: 100, sm: 160, md: 180 },
              minHeight: { xs: 48, sm: 64, md: 64 },
              fontSize: {
                xs: '0.95rem',
                sm: '1.25rem',
                md: '1.25rem',
                lg: '1.15rem',
              },
              backgroundColor: 'rgba(255, 255, 255, 0.98)',
              borderColor: 'primary.main',
              borderWidth: { xs: 2, sm: 3 },
              color: 'primary.main',
              fontWeight: 600,
              borderRadius: 2,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
                borderWidth: { xs: 2, sm: 3 },
                borderColor: 'primary.dark',
                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.2)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={isPending}
            sx={{
              minWidth: { xs: 140, sm: 200, md: 220 },
              minHeight: { xs: 48, sm: 64, md: 64 },
              fontSize: {
                xs: '0.95rem',
                sm: '1.25rem',
                md: '1.25rem',
                lg: '1.15rem',
              },
              fontWeight: 700,
              borderRadius: 2,
              boxShadow: '0 6px 24px rgba(0, 0, 0, 0.3)',
              '&:hover': {
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
              },
              '&.Mui-disabled': {
                backgroundColor: 'primary.main',
                color: 'white',
              },
            }}
          >
            {isPending ? (
              <>
                Submitting...
                <CircularProgress
                  color="inherit"
                  size={24}
                  thickness={5}
                  sx={{ ml: 1.5 }}
                />
              </>
            ) : (
              'Submit Review'
            )}
          </Button>
        </Box>

        {/* Error Snackbar */}
        <Snackbar
          open={showError}
          autoHideDuration={6000}
          onClose={handleCloseError}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseError}
            severity="error"
            sx={{
              width: '100%',
              fontSize: { xs: '0.9rem', sm: '1rem' },
            }}
          >
            {errorMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default LoungeSurveyScreen;
